import { useState, useCallback } from 'react';
import type { GooglePayDeliveryOptionsResponse } from '@/types/googlePay';
import { getAntiForgeryToken } from '@/utils/getAntiForgeryToken';

// Cache for delivery options by country
const deliveryOptionsCache = new Map<string, GooglePayDeliveryOptionsResponse>();

// Note: Default options will be fetched from the backend

export const useGooglePayDeliveryOptions = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;

  const fetchDeliveryOptions = useCallback(async (
    countryCode: string, 
    isBuyNow?: boolean, 
    productCode?: string
  ): Promise<GooglePayDeliveryOptionsResponse> => {
    // Create cache key including Buy Now parameters for proper caching
    const cacheKey = `${countryCode}_${isBuyNow ? 'buynow' : 'regular'}_${productCode || 'none'}`;
    
    // TEMPORARILY DISABLED: Check cache first - this was preventing fresh data fetches
    // const cached = deliveryOptionsCache.get(cacheKey);
    // if (cached) {
    //   console.log('GooglePay: Using cached delivery options for', cacheKey, cached);
    //   return cached;
    // }

    // Default to Ireland if no country specified
    const targetCountry = countryCode || 'IE';
    
    console.log('GooglePay: Fetching delivery options for country:', targetCountry, 'isBuyNow:', isBuyNow, 'productCode:', productCode);

    setLoading(true);
    setError(null);

    try {
      // Build URL with optional parameters
      let fetchUrl = `/Checkout/GetShippingOptions?countryCode=${targetCountry}`;
      
      if (isBuyNow === true) {
        fetchUrl += `&isBuyNow=true`;
        if (productCode) {
          fetchUrl += `&productCode=${encodeURIComponent(productCode)}`;
          // Generate cart name for Buy Now mode
          const buyNowCartName = `GooglePay_BuyNow_${productCode}`;
          fetchUrl += `&buyNowCartName=${encodeURIComponent(buyNowCartName)}`;
        }
      }
      
      console.log('GooglePay: Making request to:', fetchUrl);
      
      const response = await fetch(fetchUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'RequestVerificationToken': getAntiForgeryToken()
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch delivery options: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('GooglePay: Raw controller response:', JSON.stringify(data, null, 2));
      
      // The backend returns a response with structure:
      // {
      //   "data": [ { title, description, price, type } ],
      //   "isSuccess": boolean,
      //   "messages": []
      // }
      const responseData = data.data || data;
      console.log('GooglePay: Extracted responseData:', responseData);
      const shippingMethods = Array.isArray(responseData) ? responseData : [];
      console.log('GooglePay: Shipping methods array:', shippingMethods);
      
      const mappedOptions = shippingMethods.map((method: any, index: number) => {
        // Extract numeric price from formatted string (e.g., "€5.99" -> 5.99)
        const priceMatch = method.price?.match(/[\d.,]+/);
        const priceValue = priceMatch ? parseFloat(priceMatch[0].replace(',', '.')) : 0;
        
        console.log('GooglePay: Processing shipping method:', {
          index,
          originalMethod: method,
          type: method.type,
          title: method.title,
          price: method.price,
          priceValue: priceValue
        });
        
        const mappedOption = {
          id: method.type || `shipping_${index}`,
          label: method.title || 'Shipping',
          description: method.description || method.price || '',
          // Note: We store the decimal value here, it will be formatted for Google Pay later
          amount: priceValue.toFixed(2),
          isDefault: index === 0 // First option is default
        };
        
        console.log('GooglePay: Mapped option:', mappedOption);
        return mappedOption;
      });
      
      console.log('GooglePay: All mapped options:', mappedOptions);
      
      const mappedResponse: GooglePayDeliveryOptionsResponse = {
        options: mappedOptions,
        countryCode: targetCountry,
        isDeliverySupported: data.isSuccess !== false && mappedOptions.length > 0,
        unsupportedMessage: mappedOptions.length === 0 ? 'No delivery options available for this country' : undefined,
        success: data.isSuccess !== false,
        messages: data.messages || []
      };
      
      console.log('GooglePay: Final mapped response:', mappedResponse);
      
      // Cache the result with proper cache key
      deliveryOptionsCache.set(cacheKey, mappedResponse);
      console.log('GooglePay: Cached response for key:', cacheKey);
      
      // Reset retry count on success
      setRetryCount(0);
      
      return mappedResponse;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch delivery options';
      setError(errorMessage);
      console.error('Error fetching delivery options:', err);
      
      // Retry logic for transient failures
      if (retryCount < MAX_RETRIES) {
        console.log(`Retrying delivery options fetch (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        setRetryCount(prev => prev + 1);
        
        // Wait before retry (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
        
        // Clear error and retry
        setError(null);
        return fetchDeliveryOptions(targetCountry, isBuyNow, productCode);
      }
      
      // Return error response after all retries failed
      return {
        options: [],
        countryCode: targetCountry,
        isDeliverySupported: false,
        unsupportedMessage: 'Unable to load delivery options. Please try again.',
        success: false,
        messages: [errorMessage]
      };
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  const getDefaultOptions = useCallback(async () => {
    // Fetch default options for Ireland
    return await fetchDeliveryOptions('IE');
  }, [fetchDeliveryOptions]);

  const clearCache = useCallback(() => {
    deliveryOptionsCache.clear();
  }, []);

  return {
    fetchDeliveryOptions,
    getDefaultOptions,
    clearCache,
    loading,
    error
  };
};